import { useSyncExternalStore } from 'react';
import type { KeyboardShortcut } from './useKeyboardShortcuts';

/**
 * Live registry of every keyboard shortcut currently bound.
 *
 * The shortcuts dialog used to be built from a hand-maintained list. It drifted
 * twice: it advertised Ctrl+N and Del, which were registered nowhere and did
 * nothing when pressed, and it kept describing Shift+? after that binding had
 * been deliberately removed. A reference that lies is worse than no reference —
 * the user concludes the feature is broken rather than absent.
 *
 * So the dialog reads this instead. Every `useKeyboardShortcuts` call publishes
 * what it binds and withdraws it on unmount, which makes the documentation a
 * consequence of registration rather than a parallel claim about it. It also
 * means page-scoped shortcuts appear exactly while their page is mounted, which
 * is when they work.
 *
 *
 * WHY THE SIGNATURE CHECK EXISTS
 *
 * The first version pushed the caller's array straight in and notified on every
 * change. That deadlocks: most callers build their shortcut array inline, so it
 * has a fresh identity on every render — register, notify, subscriber
 * re-renders, new array, register again. "Maximum update depth exceeded",
 * reproducibly, for any caller who did not happen to wrap theirs in useMemo.
 *
 * Requiring every caller to memoise would be a trap rather than a fix. So each
 * hook instance owns a mutable slot, and listeners are notified only when the
 * *observable* content changes — the ids, keys, modifiers and labels the dialog
 * actually renders. A re-registered equivalent array is a no-op, and the
 * snapshot keeps its identity so `useSyncExternalStore` bails out.
 */

interface Slot {
  shortcuts: KeyboardShortcut[];
  /** The hook's master switch. A disabled slot neither fires nor documents. */
  enabled: boolean;
}

const slots = new Set<Slot>();
const listeners = new Set<() => void>();

let snapshot: KeyboardShortcut[] = [];
let signature = '';

/** What the dialog can actually see. Two lists with the same one are identical to it. */
function signatureOf(shortcuts: KeyboardShortcut[]): string {
  return shortcuts
    .map((s) => `${s.id}|${s.key}|${!!s.ctrl}|${!!s.shift}|${!!s.alt}|${s.label}|${s.category}`)
    .join(';');
}

/**
 * Rebuilds the snapshot and notifies only if it changed.
 *
 * Disabled entries are dropped — a shortcut carrying `enabled: false` does not
 * fire, so listing it would reintroduce the exact problem this replaced.
 * Duplicate ids collapse to one row: a page can legitimately register the same
 * logical shortcut as the layout, and the dialog wants one line per binding.
 */
function recompute() {
  const seen = new Set<string>();
  const next: KeyboardShortcut[] = [];

  for (const slot of slots) {
    if (!slot.enabled) continue;
    for (const shortcut of slot.shortcuts) {
      if (shortcut.enabled === false) continue;
      if (seen.has(shortcut.id)) continue;
      seen.add(shortcut.id);
      next.push(shortcut);
    }
  }

  const nextSignature = signatureOf(next);
  if (nextSignature === signature) return;

  signature = nextSignature;
  snapshot = next;
  for (const listener of listeners) listener();
}

/**
 * Claims a slot for one hook instance. The returned updater replaces its
 * contents; the returned release drops it.
 */
export function createShortcutSlot(): {
  update: (shortcuts: KeyboardShortcut[], enabled: boolean) => void;
  release: () => void;
} {
  const slot: Slot = { shortcuts: [], enabled: true };
  slots.add(slot);
  installDispatcher();

  return {
    update(shortcuts, enabled) {
      slot.shortcuts = shortcuts;
      slot.enabled = enabled;
      recompute();
    },
    release() {
      slots.delete(slot);
      recompute();
      if (slots.size === 0) removeDispatcher();
    },
  };
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

/**
 * One listener for the whole app, resolving a key to exactly one handler.
 *
 * Each hook instance used to install its own window listener. Two of them
 * binding the same key therefore both fired: `stopPropagation` on a
 * capture-phase window listener stops propagation to other NODES, not to other
 * listeners on the same node. Measured, not assumed — Resident Detail's Ctrl+S
 * and a tab's Ctrl+S both ran, which would have saved two different records
 * from one keystroke. That is why Ctrl+S was never rolled out beyond two pages
 * without this.
 *
 * Slots are walked newest-first, so the innermost mounted scope wins and
 * everything behind it stays quiet — the behaviour people expect of a dialog
 * or panel that takes over a key while it is on screen.
 */
let dispatching = false;

function isEditableTarget(e: KeyboardEvent): boolean {
  // A keydown can target `window` or `document` — neither has getAttribute or
  // tagName. The old per-hook listeners only existed while some page had
  // shortcuts mounted, so this was rarely reached with a non-element target;
  // one shared dispatcher meets every such event and threw
  // "el.getAttribute is not a function" out of an unhandled error.
  const el = e.target;
  if (!el || !(el instanceof Element)) return false;

  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  // MUI autocomplete combobox
  if (el.getAttribute('role') === 'combobox') return true;
  return false;
}

function matches(e: KeyboardEvent, s: KeyboardShortcut): boolean {
  if (s.enabled === false) return false;
  if (e.key.toLowerCase() !== s.key.toLowerCase()) return false;
  if (!!s.ctrl !== (e.ctrlKey || e.metaKey)) return false;
  if (!!s.shift !== e.shiftKey) return false;
  if (!!s.alt !== e.altKey) return false;
  return true;
}

/**
 * Is a modal currently on screen and interactive?
 *
 * MUI unmounts a closed Modal unless `keepMounted` is set, in which case it
 * stays in the DOM carrying `.MuiModal-hidden` — so presence alone is not
 * enough. `dialog[open]` covers the native element.
 *
 * `[aria-modal="true"]` covers hand-rolled dialogs — a positioned div with a
 * backdrop, which several screens use instead of MUI. They were invisible here,
 * so a page shortcut kept firing underneath an open dialog: Escape inside a
 * Selective Touch note dialog ran the page's "go back a screen" handler as well
 * as closing the dialog. A dialog owns the keyboard whoever built it, and
 * `aria-modal` is the same attribute that tells a screen reader so — opt-in per
 * dialog rather than by class name, so marking one up correctly fixes both at
 * once.
 */
function isModalOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector(
    '.MuiModal-root:not(.MuiModal-hidden), dialog[open], [aria-modal="true"]',
  ) !== null;
}

function dispatch(e: KeyboardEvent) {
  const inInput = isEditableTarget(e);
  // Newest slot first: the innermost scope owns the key.
  const ordered = [...slots].reverse();

  for (const slot of ordered) {
    if (!slot.enabled) continue;

    for (const shortcut of slot.shortcuts) {
      if (!matches(e, shortcut)) continue;
      if (inInput && !shortcut.allowInInput) continue;

      // A modal owns the keyboard. This runs on `window` in the CAPTURE phase,
      // ahead of the dialog's own handler, so letting a page shortcut through
      // means the page acts on a keystroke aimed at the dialog in front of the
      // user. A shortcut belonging to the dialog itself opts in with
      // `whenModalOpen: 'fire'`.
      //
      // isModalOpen() queries the DOM, so it is reached only once a shortcut
      // has actually matched rather than on every keystroke.
      if (shortcut.whenModalOpen !== 'fire' && isModalOpen()) return;

      e.preventDefault();
      e.stopPropagation();
      shortcut.handler(e);
      return;
    }
  }
}

function onKeyDown(e: KeyboardEvent) {
  if (dispatching) return;
  dispatching = true;
  try {
    dispatch(e);
  } finally {
    dispatching = false;
  }
}

function installDispatcher() {
  if (typeof window === 'undefined') return;
  if (slots.size > 1) return; // already installed by the first slot
  window.addEventListener('keydown', onKeyDown, true);
}

function removeDispatcher() {
  if (typeof window === 'undefined') return;
  window.removeEventListener('keydown', onKeyDown, true);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): KeyboardShortcut[] {
  return snapshot;
}

/** Every shortcut bound right now, deduplicated by id, disabled ones omitted. */
export function useRegisteredShortcuts(): KeyboardShortcut[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Runs a bound shortcut by id: a button that does what a key does, without a
 * second copy of the handler. False when nothing with that id is bound.
 */
export function runRegisteredShortcut(id: string): boolean {
  const shortcut = snapshot.find((s) => s.id === id);
  if (!shortcut) return false;
  shortcut.handler();
  return true;
}

/**
 * Registers a fixed set of shortcuts outside a React hook lifecycle.
 *
 * For hand-rolled listeners that dispatch their own keys but should still be
 * documented — TopToolbar's search bindings are the case that needed it.
 */
export function registerShortcuts(shortcuts: KeyboardShortcut[]): () => void {
  const slot = createShortcutSlot();
  slot.update(shortcuts, true);
  return slot.release;
}

/** Test seam — drops everything without waiting for unmount effects. */
export function __resetShortcutRegistry() {
  slots.clear();
  removeDispatcher();
  recompute();
}
